import { Accordion } from '@ui/Accordion/Accordion';
import Checkbox from '@ui/Table/Checkbox';
import { get, groupBy, snakeCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface PermissionGroup {
    title: string;
    description: string;
    permissions: string[];
    group?: string;
}

interface Props {
    /**
     * Permission keys the server offers for this club. The bundles below are hardcoded and
     * therefore also list rights of modules the club has hidden — and a few that no longer
     * exist in the config at all, which the assign endpoint silently drops anyway.
     */
    availablePermissions: string[];
}

const PredefinedPermissionGroups = ({ availablePermissions }: Props) => {
    const { t } = useTranslation('general');
    const { watch, setValue } = useFormContext();
    const permissions = watch();

    const groups = useMemo(
        (): PermissionGroup[] => [
            {
                title: t('permissions.predefined.items.generalSettings.title'),
                description: t('permissions.predefined.items.generalSettings.description'),
                permissions: [
                    'club.settings',
                    'club.mollie',
                    'club.text_template.view',
                    'club.text_template.create',
                    'club.text_template.update',
                    'club.text_template.delete'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.trainingsplanWrite.title'),
                description: t('permissions.predefined.items.trainingsplanWrite.description'),
                permissions: [
                    'trainingsplan.view',
                    'trainingsplan.create',
                    'trainingsplan.delete',
                    'trainingsplan.settings',
                    'trainingsplan.import',
                    'trainingsplan.notify',
                    'trainingsplan.trainer.view',
                    'trainingsplan.trainer.create',
                    'trainingsplan.groups.view',
                    'trainingsplan.groups.upsert',
                    'trainingsplan.groups.delete',
                    'trainingsplan.groups.move',
                    'trainingsplan.users.view',
                    'trainingsplan.users.upsert',
                    'trainingsplan.users.delete',
                    'trainingsplan.tags.view',
                    'trainingsplan.tags.upsert',
                    'trainingsplan.tags.delete',
                    'trainingsplan.tags.assign'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.memberManagementWrite.title'),
                description: t('permissions.predefined.items.memberManagementWrite.description'),
                permissions: [
                    'member_management.view',
                    'member_management.file',
                    'member_management.create',
                    'member_management.update',
                    'member_management.service_hours.update',
                    'member_management.delete',
                    'member_management.statistics',
                    'member_management.restore',
                    'member_management.export',
                    'member_management.settings',
                    'member_management.cancellations',
                    'member_management.property.view',
                    'member_management.property.create',
                    'member_management.property.update',
                    'member_management.property.delete',
                    'member_management.group.view',
                    'member_management.group.create',
                    'member_management.group.update',
                    'member_management.group.delete',
                    'member_management.group.unassign',
                    'member_management.category.view',
                    'member_management.category.create',
                    'member_management.category.update',
                    'member_management.category.assign',
                    'member_management.category.delete',
                    'member_management.application.view',
                    'member_management.application.update',
                    'member_management.letter.view',
                    'member_management.letter.create',
                    'member_management.letter.update',
                    'member_management.letter.delete'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.invoicesWrite.title'),
                description: t('permissions.predefined.items.invoicesWrite.description'),
                permissions: [
                    'user_invoice.view',
                    'user_invoice.create',
                    'user_invoice.update',
                    'user_invoice.delete',
                    'user_invoice.restore',
                    'user_invoice.move',
                    'user_invoice.ship',
                    'user_invoice.import',
                    'user_invoice.issuer.create',
                    'user_invoice.issuer.update',
                    'user_invoice.issuer.delete',
                    'user_invoice.sepa.create',
                    'user_invoice.sepa.update',
                    'user_invoice.sepa.delete',
                    'user_invoice.category.create',
                    'user_invoice.category.update',
                    'user_invoice.category.delete'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.trainerBookingWrite.title'),
                description: t('permissions.predefined.items.trainerBookingWrite.description'),
                permissions: [
                    'trainer_booking.settings',
                    'trainer_booking.trainer.create',
                    'trainer_booking.trainer.update',
                    'trainer_booking.trainer.delete',
                    'trainer_booking.price_rule.view',
                    'trainer_booking.price_rule.create',
                    'trainer_booking.price_rule.update',
                    'trainer_booking.price_rule.delete',
                    'trainer_booking.blocking.view',
                    'trainer_booking.blocking.create',
                    'trainer_booking.blocking.update',
                    'trainer_booking.blocking.delete',
                    'trainer_booking.blocking_group.view',
                    'trainer_booking.blocking_group.create',
                    'trainer_booking.blocking_group.update',
                    'trainer_booking.blocking_group.delete',
                    'trainer_booking.booking.view',
                    'trainer_booking.booking.create',
                    'trainer_booking.booking.update',
                    'trainer_booking.booking.delete',
                    'trainer_booking.booking.cancel',
                    'trainer_booking.booking.confirm'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.trainingsjournalWrite.title'),
                description: t('permissions.predefined.items.trainingsjournalWrite.description'),
                permissions: [
                    'trainingsjournal.template.view',
                    'trainingsjournal.template.create',
                    'trainingsjournal.template.update',
                    'trainingsjournal.template.delete',
                    'trainingsjournal.template.cancel',
                    'trainingsjournal.template.delay',
                    'trainingsjournal.template.import',
                    'trainingsjournal.entry.view',
                    'trainingsjournal.entry.create',
                    'trainingsjournal.entry.update',
                    'trainingsjournal.entry.delete',
                    'trainingsjournal.invoice-pattern.view',
                    'trainingsjournal.invoice-pattern.create',
                    'trainingsjournal.invoice-pattern.update',
                    'trainingsjournal.invoice-pattern.delete',
                    'trainingsjournal.import.view',
                    'trainingsjournal.import.update',
                    'trainingsjournal.import.delete',
                    'trainingsjournal.trainer.cancel',
                    'trainingsjournal.trainer.reschedule',
                    'trainingsjournal.trainer.message',
                    'trainingsjournal.trainer.manual',
                    'trainingsjournal.trainer.substitute',
                    'trainingsjournal.trainer.edit',
                    'trainingsjournal.trainer.delete',
                    'trainingsjournal.analysis.trainer',
                    'trainingsjournal.analysis.user',
                    'trainingsjournal.analysis.group'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.spaceBookingWrite.title'),
                description: t('permissions.predefined.items.spaceBookingWrite.description'),
                permissions: [
                    'space_booking.settings',
                    'space_booking.space.create',
                    'space_booking.space.update',
                    'space_booking.space.delete',
                    'space_booking.group.create',
                    'space_booking.group.update',
                    'space_booking.group.delete',
                    'space_booking.price_rule.view',
                    'space_booking.price_rule.create',
                    'space_booking.price_rule.update',
                    'space_booking.price_rule.delete',
                    'space_booking.blocking.view',
                    'space_booking.blocking.create',
                    'space_booking.blocking.update',
                    'space_booking.blocking.delete',
                    'space_booking.blocking_group.view',
                    'space_booking.blocking_group.create',
                    'space_booking.blocking_group.update',
                    'space_booking.blocking_group.delete',
                    'space_booking.booking.view',
                    'space_booking.booking.create',
                    'space_booking.booking.update',
                    'space_booking.booking.delete',
                    'space_booking.booking.cancel',
                    'space_booking.booking.confirm',
                    'space_booking.subscription.view',
                    'space_booking.subscription.create',
                    'space_booking.subscription.update',
                    'space_booking.subscription.delete',
                    'space_booking.voucher_card.view',
                    'space_booking.voucher_card.create',
                    'space_booking.voucher_card.update',
                    'space_booking.voucher_card.delete',
                    'space_booking.iot.view',
                    'space_booking.iot.device.view',
                    'space_booking.iot.output.change',
                    'space_booking.iot.output.view',
                    'space_booking.iot.output.create',
                    'space_booking.iot.output.update'
                ],
                group: t('permissions.predefined.groups.readWrite')
            },
            {
                title: t('permissions.predefined.items.trainingsplanRead.title'),
                description: t('permissions.predefined.items.trainingsplanRead.description'),
                permissions: [
                    'trainingsplan.view',
                    'trainingsplan.trainer.view',
                    'trainingsplan.groups.view',
                    'trainingsplan.users.view',
                    'trainingsplan.tags.view'
                ],
                group: t('permissions.predefined.groups.readOnly')
            },
            {
                title: t('permissions.predefined.items.memberManagementRead.title'),
                description: t('permissions.predefined.items.memberManagementRead.description'),
                permissions: [
                    'member_management.view',
                    'member_management.property.view',
                    'member_management.group.view',
                    'member_management.category.view',
                    'member_management.application.view',
                    'member_management.letter.view',
                    'member_management.file',
                    'member_management.statistics',
                    'member_management.export'
                ],
                group: t('permissions.predefined.groups.readOnly')
            },
            {
                title: t('permissions.predefined.items.invoicesRead.title'),
                description: t('permissions.predefined.items.invoicesRead.description'),
                permissions: ['user_invoice.view'],
                group: t('permissions.predefined.groups.readOnly')
            },
            {
                title: t('permissions.predefined.items.trainerBookingRead.title'),
                description: t('permissions.predefined.items.trainerBookingRead.description'),
                permissions: [
                    'trainer_booking.price_rule.view',
                    'trainer_booking.blocking.view',
                    'trainer_booking.blocking_group.view',
                    'trainer_booking.booking.view'
                ],
                group: t('permissions.predefined.groups.readOnly')
            },
            {
                title: t('permissions.predefined.items.trainingsjournalRead.title'),
                description: t('permissions.predefined.items.trainingsjournalRead.description'),
                permissions: [
                    'trainingsjournal.template.view',
                    'trainingsjournal.entry.view',
                    'trainingsjournal.invoice-pattern.view',
                    'trainingsjournal.import.view',
                    'trainingsjournal.analysis.trainer',
                    'trainingsjournal.analysis.user',
                    'trainingsjournal.analysis.group'
                ],
                group: t('permissions.predefined.groups.readOnly')
            },
            {
                title: t('permissions.predefined.items.spaceBookingRead.title'),
                description: t('permissions.predefined.items.spaceBookingRead.description'),
                permissions: [
                    'space_booking.price_rule.view',
                    'space_booking.blocking.view',
                    'space_booking.blocking_group.view',
                    'space_booking.booking.view',
                    'space_booking.subscription.view',
                    'space_booking.voucher_card.view',
                    'space_booking.iot.view'
                ],
                group: t('permissions.predefined.groups.readOnly')
            }
        ],
        [t]
    );

    // Narrow every bundle to the rights this club actually has, and drop the ones left empty:
    // a bundle whose checkbox toggles nothing is worse than no bundle at all.
    const visibleGroups = useMemo(() => {
        const available = new Set(availablePermissions);

        return groups
            .map((group) => ({ ...group, permissions: group.permissions.filter((key) => available.has(key)) }))
            .filter((group) => group.permissions.length > 0);
    }, [groups, availablePermissions]);

    const groupIsSelected = useCallback(
        (group: PermissionGroup) => {
            let state: boolean | null = undefined;

            group.permissions.forEach((permission) => {
                const permissionState = get(permissions, permission);
                if ((state === undefined || state === true) && permissionState === true) state = true;
                else if ((state === undefined || state === false) && permissionState === false) state = false;
                else state = null;
            });

            return state;
        },
        [permissions]
    );

    const handleGroupSelect = useCallback(
        (group: PermissionGroup, state: boolean | null) => {
            group.permissions.forEach((permission) => {
                setValue(permission, state);
            });
        },
        [setValue]
    );

    return (
        <div>
            {Object.entries(groupBy(visibleGroups, 'group')).map(([groupName, subGroups]) => (
                <Accordion title={groupName} key={groupName}>
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        {/* @ts-ignore */}
                        {subGroups.map((group) => (
                            <div key={group.title} className="tw-card-flat">
                                <h5 className="tw-font-bold">{group.title}</h5>
                                <p>{group.description}</p>
                                <div className="tw-flex tw-items-center tw-gap-4">
                                    <Checkbox
                                        aria-labelledby={snakeCase(group.title) + '_true'}
                                        isSelected={groupIsSelected(group) === true}
                                        onChange={(isSelected) =>
                                            handleGroupSelect(group, isSelected === false ? null : true)
                                        }
                                    />

                                    <label className="tw-mb-0 tw-text-center">{t('permissions.common.allow')}</label>
                                    <Checkbox
                                        aria-labelledby={snakeCase(group.title) + '_false'}
                                        isSelected={groupIsSelected(group) === false}
                                        onChange={(isSelected) =>
                                            handleGroupSelect(group, isSelected === false ? null : false)
                                        }
                                    />
                                    <label className="tw-mb-0 tw-text-center">{t('permissions.common.deny')}</label>
                                </div>
                            </div>
                        ))}
                    </div>
                </Accordion>
            ))}
        </div>
    );
};

export default PredefinedPermissionGroups;
